import { useCallback, useRef } from 'react';

import { useTrackAgentPanelOpened, type WorkspaceAgentPanelOpenedLocation } from '@air/analytics';
import { type Clip } from '@air/api/types';

import {
  getWorkspaceAgentHeroStructuredDisplay,
  getWorkspaceAgentPanelZeroStateSuggestionById,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelZeroStateSuggestions';
import { useHydrateWorkspaceSelectionForClipIds } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useHydrateWorkspaceSelectionForClipIds';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';
import { getWorkspaceAgentExplicitClipSelectionFromStore } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentExplicitClipSelectionFromStore';
import { type WorkspaceAgentComposerSeedSource } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerSeedProvider';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useAirStore } from '~/utils/ReduxUtils';

export type LaunchWorkspaceAgentHeroSuggestionOptions = {
  clipIds?: Clip['id'][];
  source?: Exclude<WorkspaceAgentComposerSeedSource, 'guided_flow'>;
};

const resolveAgentPanelOpenedLocation = (
  source: WorkspaceAgentComposerSeedSource,
): WorkspaceAgentPanelOpenedLocation => {
  if (source === 'selection_menu' || source === 'edit_tags_modal' || source === 'announcement') {
    return source;
  }

  return 'toolbar';
};

export const useLaunchWorkspaceAgentHeroSuggestion = () => {
  const store = useAirStore();
  const { currentWorkspace } = useCurrentWorkspace();
  const currentWorkspaceIdRef = useRef(currentWorkspace?.id);

  currentWorkspaceIdRef.current = currentWorkspace?.id;

  const hydrateSelectionForClipIds = useHydrateWorkspaceSelectionForClipIds();
  const dock = useWorkspaceAgentPanelStore((state) => state.dock);
  const startNewThreadWithHeroSeed = useWorkspaceAgentPanelStore((state) => state.startNewThreadWithHeroSeed);
  const showChat = useWorkspaceAgentPanelStore((state) => state.showChat);
  const { blockIfCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
  const { trackAgentPanelOpened } = useTrackAgentPanelOpened();

  const launchHero = useCallback(
    async (suggestionId: string, options?: LaunchWorkspaceAgentHeroSuggestionOptions) => {
      const suggestion = getWorkspaceAgentPanelZeroStateSuggestionById(suggestionId);

      if (!suggestion) {
        return;
      }

      if (blockIfCreditsDepleted(() => showChat({ focusComposer: false }))) {
        return;
      }

      const launchWorkspaceId = currentWorkspaceIdRef.current;

      if (!launchWorkspaceId) {
        return;
      }

      let hasExplicitClipSelection = false;

      if (options?.clipIds?.length) {
        const hydrateResult = await hydrateSelectionForClipIds(options.clipIds, launchWorkspaceId);

        hasExplicitClipSelection =
          hydrateResult.hasFullClipSelection && currentWorkspaceIdRef.current === launchWorkspaceId;
      } else {
        hasExplicitClipSelection = getWorkspaceAgentExplicitClipSelectionFromStore(store).hasExplicitClipSelection;
      }

      if (currentWorkspaceIdRef.current !== launchWorkspaceId) {
        return;
      }

      const structuredDisplay = getWorkspaceAgentHeroStructuredDisplay(suggestionId);
      const wasMinimized = dock === 'minimized';
      const source = options?.source ?? 'selection_menu';

      startNewThreadWithHeroSeed('toolbar', {
        prompt: suggestion.prompt,
        sendImmediately: hasExplicitClipSelection,
        source,
        structuredDisplay: hasExplicitClipSelection ? structuredDisplay : undefined,
        workspaceId: launchWorkspaceId,
      });

      if (wasMinimized) {
        trackAgentPanelOpened({ location: resolveAgentPanelOpenedLocation(source) });
      }
    },
    [
      blockIfCreditsDepleted,
      dock,
      hydrateSelectionForClipIds,
      showChat,
      startNewThreadWithHeroSeed,
      store,
      trackAgentPanelOpened,
    ],
  );

  return { launchHero };
};
